import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Button, Input, Select, SelectItem, useDisclosure } from '@heroui/react';
import { PageHeader } from '@/components/common/PageHeader';
import { Pagination } from '@/components/common/Pagination';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { EmptyState } from '@/components/common/EmptyState';
import { ErrorState } from '@/components/common/ErrorState';
import { useTableQueryState } from '@/hooks/useTableQueryState';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { useMailboxesList, useDeleteMailbox, useMailboxDomainSummary } from '@/features/mailboxes/hooks';
import { MailboxesTable } from '@/features/mailboxes/components/MailboxesTable';
import { MailboxDomainGroups } from '@/features/mailboxes/components/MailboxDomainGroups';
import { MailboxFormModal } from '@/features/mailboxes/components/MailboxFormModal';
import { MailboxResetPasswordModal } from '@/features/mailboxes/components/MailboxResetPasswordModal';
import { groupMailboxes } from '@/features/mailboxes/groupMailboxes';
import type { Mailbox } from '@/features/mailboxes/types';
import { PlusIcon, MagnifyingGlassIcon, FunnelIcon, GlobeAltIcon } from '@/icons';
import { useOpenOnNewParam } from '@/hooks/useOpenOnNewParam';
import { usePermission } from '@/hooks/usePermission';
import { useCreateOwnerWebmailSession, usePlatformCapabilities } from '@/features/platform/hooks';
import { beginOwnerInboxHandoff, submitOwnerInboxHandoff } from '@/features/platform/ownerInboxHandoff';
import { apiErrorMessage, isApiError } from '@/api/errors';
import { OwnerInboxStepUpModal } from '@/features/platform/components/OwnerInboxStepUpModal';
import { notify } from '@/lib/notify';

/** Rows loaded for the all-domains view; larger domains link to their own paginated view. */
const GROUPED_PAGE_SIZE = 200;
const ALL = 'all';

export function MailboxesPage() {
  const { state, setPage, setPageSize, setSearch, toggleSort, setFilter } = useTableQueryState(['status', 'domain']);
  const [term, setTerm] = useState(state.q);
  const debounced = useDebouncedValue(term, 300);
  useMemo(() => { if (debounced !== state.q) setSearch(debounced); }, [debounced]); // eslint-disable-line

  const summary = useMailboxDomainSummary();
  const domains = useMemo(() => [...(summary.data ?? [])].sort((a, b) => a.domain.localeCompare(b.domain)), [summary.data]);
  const domainId = state.filters.domain && domains.some((d) => d.domainId === state.filters.domain) ? state.filters.domain : undefined;
  const grouped = !domainId;

  const list = useMailboxesList(grouped
    ? { page: 1, pageSize: GROUPED_PAGE_SIZE, q: state.q || undefined, status: state.filters.status }
    : { page: state.page, pageSize: state.pageSize, q: state.q || undefined, sort: state.sort || undefined, status: state.filters.status, domainId });

  const groups = useMemo(
    () => (grouped ? groupMailboxes(domains, list.data?.data ?? [], state.q) : []),
    [grouped, domains, list.data, state.q],
  );
  const total = domains.reduce((n, d) => n + d.mailboxCount, 0);

  const form = useDisclosure();
  const resetModal = useDisclosure();
  const [target, setTarget] = useState<Mailbox | null>(null);
  const [createDomain, setCreateDomain] = useState<string | undefined>();
  const { can } = usePermission();
  const canCreate = can('mailbox:create');
  const canManage = can('mailbox:manage');
  const { canAccessMailboxInbox } = usePlatformCapabilities();
  const ownerWebmail = useCreateOwnerWebmailSession();
  const [stepUpFor, setStepUpFor] = useState<Mailbox | null>(null);
  const openInbox = async (mailbox: Mailbox) => {
    // Open the browsing context synchronously from the user's click. Waiting
    // until after the API call makes browsers treat it as a scripted popup.
    const inboxWindow = beginOwnerInboxHandoff();
    if (!inboxWindow) {
      notify.error('Your browser blocked the inbox tab. Allow pop-ups for Nifty Hosting and try again.');
      return;
    }
    try {
      const session = await ownerWebmail.mutateAsync(mailbox);
      submitOwnerInboxHandoff(inboxWindow, session);
    } catch (error) {
      inboxWindow.close();
      if (isApiError(error) && error.code === 'AUTH_STEP_UP_REQUIRED') { setStepUpFor(mailbox); return; }
      notify.error(apiErrorMessage(error, 'Could not open this inbox'));
    }
  };
  const openCreate = (forDomain?: string) => { setTarget(null); setCreateDomain(forDomain ?? domainId); form.onOpen(); };
  useOpenOnNewParam(() => { if (canCreate) openCreate(); });
  const [toDelete, setToDelete] = useState<Mailbox | null>(null);
  const del = useDeleteMailbox();
  const rowActions = {
    onEdit: (m: Mailbox) => { setTarget(m); form.onOpen(); },
    onReset: (m: Mailbox) => { setTarget(m); resetModal.onOpen(); },
    onDelete: canManage ? setToDelete : undefined,
    onOpenInbox: canAccessMailboxInbox ? openInbox : undefined,
  };

  let body: JSX.Element;
  if (!grouped) {
    body = (
      <>
        <MailboxesTable isLoading={list.isLoading} sort={state.sort} onSort={toggleSort}
          // A→Z unless the user picked a column sort.
          mailboxes={state.sort ? list.data?.data ?? [] : [...(list.data?.data ?? [])].sort((a, b) => a.address.localeCompare(b.address))}
          isError={list.isError} onRetry={() => list.refetch()} {...rowActions}
          emptyMessage={state.q || state.filters.status ? 'No mailboxes on this domain match your filters.' : 'No mailboxes on this domain yet.'} />
        <Pagination page={state.page} pageSize={state.pageSize} total={list.data?.total} onPageChange={setPage} onPageSizeChange={setPageSize} />
      </>
    );
  } else if (summary.isError || list.isError) {
    body = <div className="p-6"><ErrorState error={summary.error ?? list.error} onRetry={() => { summary.refetch(); list.refetch(); }} /></div>;
  } else if (summary.isLoading || list.isLoading) {
    body = <MailboxesTable mailboxes={[]} isLoading sort="" {...rowActions} />;
  } else if (domains.length === 0 && groups.length === 0) {
    body = <EmptyState icon={GlobeAltIcon} title="No domains yet" description="Add and verify a domain before creating mailboxes."
      action={<Link to="/domains" className="rounded-lg bg-brand/10 px-4 py-2 text-sm font-medium text-brand hover:bg-brand/20">Go to Domains</Link>} />;
  } else if (groups.length === 0) {
    body = <p role="status" className="px-4 py-10 text-center text-sm text-fg-muted">No mailboxes match “{state.q}”{state.filters.status ? ' with this status' : ''}.</p>;
  } else {
    body = <MailboxDomainGroups groups={groups} searching={!!state.q || !!state.filters.status} {...rowActions}
      onShowDomain={(id) => setFilter('domain', id)} onCreateIn={canCreate ? openCreate : undefined} />;
  }

  return (
    <div>
      <PageHeader title="Mailboxes" description="Create and manage mailboxes, quotas and passwords."
        actions={canCreate ? <Button color="primary" startContent={<PlusIcon className="h-4 w-4" />} onPress={() => openCreate()}>Create mailbox</Button> : undefined} />
      <div className="rounded-xl border border-border bg-surface">
        <div className="flex flex-col gap-3 border-b border-border p-4 sm:flex-row sm:flex-wrap sm:items-center">
          <Select aria-label="Filter by domain" size="sm" className="sm:w-64" isLoading={summary.isLoading}
            startContent={<GlobeAltIcon className="h-4 w-4 text-fg-subtle" />}
            selectedKeys={new Set([domainId ?? ALL])} disallowEmptySelection
            onSelectionChange={(k) => { const v = String(Array.from(k)[0] ?? ALL); setFilter('domain', v === ALL ? '' : v); }}
            items={[{ key: ALL, label: summary.isSuccess ? `All domains (${total})` : summary.isError ? 'Domains unavailable' : 'All domains' }, ...domains.map((d) => ({ key: d.domainId, label: `${d.domain} (${d.mailboxCount})` }))]}>
            {(item) => <SelectItem key={item.key}>{item.label}</SelectItem>}
          </Select>
          <Input value={term} onValueChange={setTerm} placeholder="Search by address or name…"
            startContent={<MagnifyingGlassIcon className="h-4 w-4 text-fg-subtle" />} variant="bordered" size="sm" className="sm:max-w-xs"
            aria-label="Search mailboxes" isClearable onClear={() => { setTerm(''); setSearch(''); }} />
          <Select aria-label="Filter by status" size="sm" placeholder="All statuses" className="sm:w-40"
            startContent={<FunnelIcon className="h-4 w-4 text-fg-subtle" />}
            selectedKeys={state.filters.status ? new Set([state.filters.status]) : new Set()}
            onSelectionChange={(k) => setFilter('status', String(Array.from(k)[0] ?? ''))}>
            <SelectItem key="active">Active</SelectItem>
            <SelectItem key="suspended">Suspended</SelectItem>
          </Select>
        </div>
        {body}
      </div>

      <MailboxFormModal isOpen={form.isOpen} onClose={form.onClose} mailbox={target} defaultDomainId={createDomain} />
      <OwnerInboxStepUpModal mailbox={stepUpFor} onClose={() => setStepUpFor(null)} />
      {target && <MailboxResetPasswordModal isOpen={resetModal.isOpen} onClose={resetModal.onClose} mailbox={target} />}
      <ConfirmDialog isOpen={!!toDelete} onClose={() => setToDelete(null)}
        onConfirm={async () => { if (toDelete) await del.mutateAsync(toDelete.id); setToDelete(null); }}
        title="Delete mailbox?" body={`Permanently delete ${toDelete?.address} and its stored mail? This cannot be undone.`}
        confirmLabel="Delete" tone="danger" isLoading={del.isPending} />
    </div>
  );
}
